'use client'
import { Button } from '@/components/Button'
import { Input } from '@/components/Input'
import { Loading } from '@/components/Loading'
import { Select } from '@/components/Select'
import { SearchInput } from '@/components/SearchInput'
import { SelectCountry } from '@/components/SelectCountry'
import { usePopup } from '@/hooks/Popup'
import { Icon } from '@/icons'
import {
  HelpType,
  RegistryDatabase,
  RegistrySearchItem,
} from '@/services/Api/generated'
import { MktuManualSelect } from '@/shared/Mktu/components'
import { SearchResultItem } from '@/shared/OnlineSearch/components/SearchResultItem'
import { mktuClassesList } from '@/store/Mktu/MktuClassesList'
import { EmptyMessage } from '@/domains/WebApp/components/EmptyMessage'
import { Title } from '@/domains/WebApp/components/Title'
import { userStore } from '@/domains/WebApp/store/User'
import { ExpertHelp } from '@/widgets/ExpertHelp'
import cn from 'classnames'
import { observer } from 'mobx-react-lite'
import React, { FC, useCallback, useEffect } from 'react'
import { DetailsPopup } from './components/DetailsPopup'
import { RegistrationPrompt } from './components/RegistrationPrompt'
import { RegistryInfoStore } from './RegistryInfo.store'
import styles from './RegistrySearch.module.scss'
import { RegistrySearchStore } from './RegsitrySearch.store'

const registrySearchStore = new RegistrySearchStore()
const registryInfoStore = new RegistryInfoStore()

const OPTIONS_DATABASES: { label: string; value: RegistryDatabase }[] = [
  {
    label: 'ТЗ РФ',
    value: RegistryDatabase.Rutm,
  },
  {
    label: 'Заявки на ТЗ РФ',
    value: RegistryDatabase.Rutmap,
  },
  {
    label: 'Общеизвестные ТЗ',
    value: RegistryDatabase.Wktm,
  },
  {
    label: 'Междунаротные регистрации',
    value: RegistryDatabase.Mp,
  },
]

interface RegistrySearchProps {}
export const RegistrySearch: FC<RegistrySearchProps> = observer(() => {
  const controlRegistryInfoPopup = usePopup()
  const mktuFilterPopup = usePopup()

  useEffect(() => {
    mktuClassesList.loader.request()
  }, [])

  const showDetailInfo = useCallback(
    (registry: RegistrySearchItem) => {
      registryInfoStore.dataLoader.request({ registry })
      controlRegistryInfoPopup.show()
    },
    [controlRegistryInfoPopup]
  )

  const search = useCallback(() => {
    registrySearchStore.dataLoader.request()
    controlRegistryInfoPopup.hide()
  }, [controlRegistryInfoPopup])

  const formSubmit = useCallback(
    (event: React.SubmitEvent<HTMLFormElement>) => {
      event.preventDefault()
      search()
    },
    [search]
  )

  return (
    <>
      <Title title="Поиск товарного знака" />
      <div className={styles.header}>
        <form className={styles.form} onSubmit={formSubmit}>
          <SearchInput
            className={styles.searchInput}
            placeholder="Введите название товарного знака"
            onChange={registrySearchStore.setSearchString}
          />
          <Button
            variant="primary"
            name="Найти"
            onClick={search}
            className={styles.searchButton}
          />
          <Button
            variant="secondary"
            onlyIcon
            className={cn(styles.settingsButton)}
            onClick={mktuFilterPopup.toggle}
            title="Уточните критерии поиска"
          >
            {mktuFilterPopup.isVisible ? (
              <Icon name="close" className={styles.settingsIcon} />
            ) : (
              <Icon name="settings" className={styles.settingsIcon} />
            )}
          </Button>
        </form>
        <div
          className={cn(styles.mktuPopup, {
            [styles.isHidden]: !mktuFilterPopup.isVisible,
          })}
        >
          {userStore.isRegistered ? (
            <div className={styles.containerForm}>
              <Select
                className={styles.database}
                name="database"
                variant="comboBox"
                values={registrySearchStore.additionalFilters.databases}
                onChange={registrySearchStore.additionalFilters.setDatabase}
                options={OPTIONS_DATABASES}
                placeholder="Базы данных"
              />
              <Input
                className={cn(styles.input, styles.numberIPS)}
                value={registrySearchStore.additionalFilters.registrationNumber}
                onChange={
                  registrySearchStore.additionalFilters.setRegistrationNumber
                }
                placeholder="№ свидетельства"
                disabled={
                  registrySearchStore.additionalFilters
                    .registrationNumberDisabled
                }
                readonly={
                  registrySearchStore.additionalFilters
                    .registrationNumberDisabled
                }
                error={registrySearchStore.additionalFilters.errorText}
                permanentPlaceholder
              />
              <Input
                className={cn(styles.input, styles.numberApplication)}
                value={registrySearchStore.additionalFilters.applicationNumber}
                onChange={
                  registrySearchStore.additionalFilters.setApplicationNumber
                }
                placeholder="№ заявки"
                disabled={
                  registrySearchStore.additionalFilters
                    .applicationNumberDisabled
                }
                readonly={
                  registrySearchStore.additionalFilters
                    .applicationNumberDisabled
                }
                error={registrySearchStore.additionalFilters.errorText}
                permanentPlaceholder
              />
              <Input
                className={cn(styles.input, styles.owner)}
                value={registrySearchStore.additionalFilters.owner}
                onChange={registrySearchStore.additionalFilters.setOwner}
                placeholder="Правообладатель"
                disabled={registrySearchStore.additionalFilters.ownerDisabled}
                readonly={registrySearchStore.additionalFilters.ownerDisabled}
                error={registrySearchStore.additionalFilters.errorText}
                permanentPlaceholder
              />
              <SelectCountry className={styles.country} />
            </div>
          ) : null}
          <MktuManualSelect
            isVisible
            className={styles.mktuManualSelect}
            title="Выберите классы МКТУ"
            selected={registrySearchStore.mktuClasses}
            onChange={registrySearchStore.setMktuClasses}
            withDescription
          />
        </div>
      </div>

      <div className={styles.content}>
        {registrySearchStore.dataLoader.loading ? (
          <Loading className={styles.loader} />
        ) : null}

        {registrySearchStore.items.length != 0 ? (
          <div className={styles.list}>
            {registrySearchStore.items.map((item) => (
              <SearchResultItem
                key={item.number}
                registry={item}
                withButton
                onClick={showDetailInfo}
              />
            ))}
          </div>
        ) : null}

        {!userStore.isRegistered && registrySearchStore.items.length >= 12 ? (
          <>
            <div className={cn(styles.list, styles.withBlur)}>
              {registrySearchStore.items.slice(0, 8).map((registry, n) => (
                <SearchResultItem
                  registry={registry}
                  key={registry.number}
                  isTabletHide={n > 5}
                  isMobileHide={n > 1}
                />
              ))}
              <div className={styles.withColorMask}></div>
              <div className={styles.withBlurMask}></div>
              <RegistrationPrompt className={styles.registrationPrompt} />
            </div>
          </>
        ) : null}

        {!registrySearchStore.dataLoader.loading &&
        !mktuFilterPopup.isVisible &&
        registrySearchStore.items.length === 0 ? (
          <EmptyMessage
            text={
              <>
                Введите запрос, чтобы появились
                <br />
                товарные знаки
              </>
            }
          />
        ) : null}
      </div>
      {userStore.isRegistered ? (
        <ExpertHelp
          className={styles.expertHelp}
          type={HelpType.ModuleHelpWithSearch}
          theme="simple"
        />
      ) : null}

      {controlRegistryInfoPopup.isVisible ? (
        <DetailsPopup
          onHide={controlRegistryInfoPopup.hide}
          registryInfo={registryInfoStore.data}
          isLoading={registryInfoStore.dataLoader.loading}
        />
      ) : null}
    </>
  )
})
